/* © 2017 - Copyright of Aetonix Systems Inc - All Rights Reserved. Patent pending.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Aetonix, January 01, 2017
 * For information or permission request, email info@aetonixsystems.com
 */

import React from "react";
import {
	TouchableOpacity,
	View,
} from "react-native";
import noop from "noop3";
import ResponsiveStylesheet from "react-native-responsive-stylesheet";

import PropTypes from "prop-types";

import Style from "ae-style";

import StyledText from "./StyledText";
import StyledIcon from "./StyledIcon";

import { useIdPrefix } from "@aetonix/hooks";

const styles = ResponsiveStylesheet.createSized("min-direction", {
	0: {
		button: {
			paddingVertical: Style.layout.marginSmall,
			paddingHorizontal: Style.layout.marginContentSmall,
		},
		specialPadding: {
			bottom: 0,
		},
		bigCircle: {
			width: 138,
			height: 138,
		},
		radiusBigCircle: {
			borderRadius: 138 / 2
		},
		smallCircle: {
			width: 100,
			height: 100,
			marginHorizontal: Style.layout.marginLarge / 2
		},
		radiusSmallCircle: {
			borderRadius: 100 / 2
		},
		smallCircleSmallMargin: {
			marginHorizontal: Style.layout.marginSlim
		},
		tinyCircle: {
			width: 80,
			height: 80,
		},
		radiusTinyCircle: {
			borderRadius: 80 / 2
		},
		localVideoControlButton: {
			width: 45,
			height: 45,
		},
		radiusVideoControlButton: {
			borderRadius: 45 / 2
		},
		localVideoControlButtonBig: {
			width: 60,
			height: 60,
		},
		radiusVideoControlBig: {
			borderRadius: 60 / 2
		},
		content: {
			position: "absolute",
			top: 0,
			left: 0,
			right: 0,
			bottom: 0,
			justifyContent: "center",
			alignItems: "center",
		},
		icon: {
			flex: 26,
			justifyContent: "center",
			alignItems: "center",
		},
		text: {
			flex: 26,
			textAlign: "center",
			position: "relative",
		},
		disabled: {
			opacity: Style.look.disabled,
		},
		containerWhite: {
			backgroundColor: Style.look.background,
		},
		emptyTextView: {
			flex: 15
		}
	},
	512: {
		icon: {
			flex: 35,
			paddingBottom: 0
		},
		localVideoControlButton: {
			width: 60,
			height: 60,
		},
		radiusVideoControlButton: {
			borderRadius: 60 / 2
		},
	}
});

/**
 * This component takes the following props:
 */
CircleButton.propTypes = {
	onPress: PropTypes.func,
	title: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.element
	]),
	disabled: PropTypes.bool,
	contrast: PropTypes.bool,
	clear: PropTypes.bool,
	diameter: PropTypes.number,
	icon: PropTypes.string,
	iconSize: PropTypes.string, // style name from Style.js
	textSize: PropTypes.number,
	tiny: PropTypes.bool,
	small: PropTypes.bool,
	containerColor: PropTypes.number,
	hugeHeading: PropTypes.bool
};

export default function CircleButton(props) {
	let onPress = props.onPress;
	const title = props.title || "";

	const disabled = props.disabled;
	const contrast = props.contrast;
	const clear = props.clear;
	const diameter = props.diameter;
	const id = props.id;

	const iconName = props.icon || "dot-circle-o";
	let iconSize = props.iconSize;
	const textSize = props.textSize;
	const tiny = props.tiny;
	const localVideoControlButton = props.localVideoControlButton;
	const magnifying = props.magnifying;
	const small = props.small;
	let containerColor = props.containerColor || styles.containerWhite;
	const textBoxStyling = props.textBoxStyling;

	const children = props.children;

	const contentStyle = [styles.content];
	const styleList = [styles.button];

	if (disabled) {
		onPress = noop;
		contentStyle.push(styles.disabled);
	}

	let textColor = {
		black: true
	};
	let iconColor = {};

	if (contrast) {
		textColor = {
			contrast: true
		};
		iconColor = {
			contrast: true
		};
	}

	if(clear)
		containerColor = null;
	contentStyle.push(containerColor);

	if (!iconSize) {
		iconSize = "hugeHeading";
		styleList.push(styles.bigCircle);
		contentStyle.push(styles.radiusBigCircle);
	}

	if (small || iconSize === "small") {
		if (!iconSize) iconSize = "small";
		styleList.push(styles.smallCircle);
		contentStyle.push(styles.radiusSmallCircle);
		if (diameter) styleList.push(styles.smallCircleSmallMargin);
	}
	if (tiny || iconSize === "tiny") {
		if (!iconSize) iconSize = "tiny";
		styleList.push(styles.tinyCircle);
		contentStyle.push(styles.radiusTinyCircle);
	}
	if (localVideoControlButton || iconSize === "localVideoControlButton") {
		if (!iconSize) iconSize = "emphasized";
		styleList.push(styles.specialPadding);
		if (magnifying) {
			styleList.push(styles.localVideoControlButtonBig);
			contentStyle.push(styles.radiusVideoControlBig);
		} else {
			styleList.push(styles.localVideoControlButton);
			contentStyle.push(styles.radiusVideoControlButton);
		}
	}

	if (diameter) {
		styleList.push({
			height: diameter,
			width: diameter
		});
		contentStyle.push({
			borderRadius: diameter / 2
		});
	}

	const iconStyleList = [styles.icon];
	const textStyleList = [styles.text];

	if (textSize)
		textStyleList.push({
			fontSize: textSize
		});

	if (textBoxStyling)
		textStyleList.push(textBoxStyling);

	const renderText = localVideoControlButton ? null : (
		<StyledText style={textStyleList} compact bold {...textColor}>
			{title}
		</StyledText>
	);

	const idPrefix = useIdPrefix();

	return (
		<TouchableOpacity testID={`${idPrefix}${idPrefix ? "-" : ""}${id}`} accessibilityComponentType="button" style={styleList} onPress={onPress}>
			<View style={contentStyle}>
				<View style={iconStyleList}>
					<StyledIcon name={iconName} size={iconSize} { ...iconColor} />
				</View>
				{renderText}
			</View>
			{children}
		</TouchableOpacity>
	);
}